import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, RefreshCw, PlugZap, CheckCircle2, XCircle, Calendar, Briefcase, Code2 } from "lucide-react";
import { formatDate } from "@/lib/format";

const REMOTE_URL = "https://corerealestatetools.com";

export default function CreToolsIntegrationPanel() {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [syncRange, setSyncRange] = useState({ start: "2026-01-01", end: "2026-07-21" });
  const [dealResult, setDealResult] = useState(null);
  const [payloadPreview, setPayloadPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/integrations/cre-tools/status");
      setStatus(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const submitConnect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/integrations/cre-tools/connect", creds);
      toast.success(`Connected as ${res.data.cre_user_name || res.data.cre_email}`);
      setConnectOpen(false);
      setCreds({ email: "", password: "" });
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Core Real Estate Tools? Ongoing sync will stop.")) return;
    try {
      await api.delete("/integrations/cre-tools");
      toast.success("Disconnected");
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const res = await api.post("/integrations/cre-tools/sync", syncRange);
      toast.success(`Synced ${res.data.pushed} new, skipped ${res.data.skipped} duplicates`);
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runPull = async () => {
    setBusy(true);
    try {
      const res = await api.post("/integrations/cre-tools/pull");
      toast.success(`Pulled ${res.data.pulled} from CRE Tools, skipped ${res.data.skipped} already-known`);
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runSyncDeals = async () => {
    setBusy(true);
    setDealResult(null);
    try {
      const res = await api.post("/integrations/cre-tools/sync-deals");
      setDealResult(res.data);
      if (res.data.reason === "not_connected") {
        toast.error("Not connected");
      } else if (res.data.pushed === 0 && res.data.failed > 0) {
        toast.warning(`0 deals accepted · ${res.data.failed} failed — the receiving app may not have a /api/deals endpoint yet. Share the payload preview with their engineers.`);
      } else {
        toast.success(`Pushed ${res.data.pushed} deals · ${res.data.skipped} already synced · ${res.data.failed} failed`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const loadPayloadPreview = async () => {
    setBusy(true);
    try {
      const res = await api.get("/integrations/cre-tools/deals-payload-preview?limit=3");
      setPayloadPreview(res.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyPayload = async () => {
    if (!payloadPreview) return;
    await navigator.clipboard.writeText(JSON.stringify(payloadPreview, null, 2));
    toast.success("Copied API contract to clipboard");
  };

  return (
    <div className="border border-neutral-200 p-6 mb-6" data-testid="cre-tools-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Integration · Expenses</div>
          <h2 className="font-serif text-2xl leading-tight">Core Real Estate Tools</h2>
          <div className="text-sm text-neutral-500 mt-1">
            Sync your Ledger expenses to <a href={REMOTE_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-neutral-900">{REMOTE_URL.replace("https://","")}</a>.
            Duplicates (same date + category + amount + vendor) are skipped automatically.
          </div>
        </div>
        <div className="shrink-0">
          {loading ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Checking…</span>
          ) : status.connected ? (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700" data-testid="cre-tools-connected-badge">
              <CheckCircle2 className="h-3 w-3" strokeWidth={1.75} /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <XCircle className="h-3 w-3" strokeWidth={1.75} /> Not connected
            </span>
          )}
        </div>
      </div>

      {status.connected ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-y border-neutral-200 py-3 mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Signed In As</div>
              <div className="font-serif text-base truncate" data-testid="cre-tools-user">{status.cre_user_name || status.cre_email}</div>
              <div className="font-mono text-[10px] text-neutral-500 truncate">{status.cre_email}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Last Sync</div>
              <div className="font-mono text-sm">{status.last_sync_at ? formatDate(status.last_sync_at) : "Never"}</div>
              {status.last_sync_at && (
                <div className="text-xs text-neutral-500">
                  {status.last_sync_count} pushed · {status.last_sync_skipped || 0} skipped
                </div>
              )}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Direction</div>
              <div className="font-mono text-sm">Ledger ⇄ CRE Tools</div>
              <div className="text-xs text-neutral-500">Auto-push on every change · pull on demand</div>
            </div>
          </div>

          <div className="border border-neutral-200 p-4 mb-4" data-testid="cre-tools-sync-range">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3 flex items-center gap-1.5">
              <Calendar className="h-3 w-3" strokeWidth={1.5} /> Backfill Range
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Start</Label>
                <Input
                  type="date"
                  value={syncRange.start}
                  onChange={(e) => setSyncRange({ ...syncRange, start: e.target.value })}
                  className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="cre-tools-sync-start"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">End</Label>
                <Input
                  type="date"
                  value={syncRange.end}
                  onChange={(e) => setSyncRange({ ...syncRange, end: e.target.value })}
                  className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="cre-tools-sync-end"
                />
              </div>
              <Button
                onClick={runSync}
                disabled={busy}
                className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10"
                data-testid="cre-tools-sync-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} strokeWidth={1.5} />
                {busy ? "Working…" : "Push local → CRE Tools"}
              </Button>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3">
              <p className="text-xs text-neutral-500">
                New expenses in Ledger auto-push instantly. Click <em>Pull</em> to import anything added directly on CRE Tools.
              </p>
              <Button
                onClick={runPull}
                disabled={busy}
                variant="outline"
                className="rounded-none border-neutral-300 h-8 text-xs"
                data-testid="cre-tools-pull-button"
              >
                <RefreshCw className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                Pull from CRE Tools
              </Button>
            </div>
          </div>

          <div className="border border-neutral-200 p-4 mb-4" data-testid="cre-tools-deals-sync">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 flex items-center gap-1.5">
                  <Briefcase className="h-3 w-3" strokeWidth={1.5} /> Deals → Icon Rendering
                </div>
                <div className="text-sm text-neutral-700 leading-snug">
                  Push every deal (with its <span className="font-mono text-xs">expense_count</span> &amp; <span className="font-mono text-xs">expense_total</span>) so Core Real Estate Tools can render an expense-tracker icon on each deal card.
                </div>
              </div>
              <Button
                onClick={loadPayloadPreview}
                disabled={busy}
                variant="outline"
                className="rounded-none border-neutral-300 h-8 text-xs shrink-0"
                data-testid="cre-tools-payload-preview-button"
              >
                <Code2 className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                API contract
              </Button>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-neutral-100 pt-3">
              <div className="text-xs text-neutral-500">
                {dealResult
                  ? `Last push: ${dealResult.pushed} pushed · ${dealResult.skipped} updated · ${dealResult.failed} failed`
                  : "New deals auto-push on create · click below to backfill the full pipeline."}
              </div>
              <Button
                onClick={runSyncDeals}
                disabled={busy}
                className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-9"
                data-testid="cre-tools-sync-deals-button"
              >
                <Briefcase className={`h-4 w-4 mr-2 ${busy ? "animate-pulse" : ""}`} strokeWidth={1.5} />
                {busy ? "Working…" : "Push Deals → CRE Tools"}
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={disconnect} variant="outline" className="rounded-none border-neutral-300 text-neutral-600" data-testid="cre-tools-disconnect">
              Disconnect
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-8" data-testid="cre-tools-connect-cta">
          <PlugZap className="h-8 w-8 text-neutral-400 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-neutral-600 mb-4 max-w-md mx-auto">
            Sign in with your Core Real Estate Tools admin account to sync expenses.
          </p>
          <Button
            onClick={() => setConnectOpen(true)}
            className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white"
            data-testid="cre-tools-connect-button"
          >
            <Link2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Connect
          </Button>
        </div>
      )}

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="rounded-none border-neutral-300 max-w-md" data-testid="cre-tools-connect-dialog">
          <DialogHeader>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Connect</div>
            <DialogTitle className="font-serif text-2xl">Core Real Estate Tools</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitConnect} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</Label>
              <Input
                type="email"
                required
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
                data-testid="cre-tools-email-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Password</Label>
              <Input
                type="password"
                required
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                className="mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="cre-tools-password-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
              <Button type="button" variant="outline" onClick={() => setConnectOpen(false)} className="rounded-none border-neutral-300">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="cre-tools-submit-connect">
                {busy ? "Connecting…" : "Connect"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-none border-neutral-300 max-w-2xl" data-testid="cre-tools-payload-dialog">
          <DialogHeader>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">API Contract</div>
            <DialogTitle className="font-serif text-2xl">Deals Payload for CRE Tools</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">JSON schema to share with the Core Real Estate Tools engineering team.</DialogDescription>
          </DialogHeader>
          {payloadPreview ? (
            <div className="space-y-3">
              <div className="text-xs text-neutral-600 leading-relaxed">
                Share this shape with the Core Real Estate Tools engineers so they can accept deal records and render an expense-tracker icon on each deal card. <span className="font-mono">deal_ref</span> is the stable idempotency key — the same deal will never be duplicated.
              </div>
              <div className="border border-neutral-200 bg-neutral-50 p-3 max-h-[360px] overflow-auto">
                <pre className="text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-words" data-testid="cre-tools-payload-json">
{JSON.stringify(payloadPreview, null, 2)}
                </pre>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
                <Button variant="outline" className="rounded-none border-neutral-300" onClick={() => setPreviewOpen(false)}>Close</Button>
                <Button onClick={copyPayload} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="cre-tools-payload-copy">
                  Copy JSON
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-500 py-6">Loading preview…</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
